import "react-toastify/dist/ReactToastify.css";

import { useEffect, useState } from "react";
import { SWRConfig } from "swr";

import AudioContext from "@/contexts/AudioContext";
import { CaptureContext } from "@/contexts/CaptureContext";
import PanelContext, { usePanel } from "@/contexts/PanelContext";

import { AppContext, useAppContext } from "../contexts/AppContext";
import { DialogContext } from "../contexts/DialogContext";
import { TemplateContext } from "../contexts/TemplateContext";
import { KeybindManager } from "../lib/keybinds";
import { Moderator, ModeratorContext } from "../Moderator/Moderator";
import { AuthErrors } from "./AuthErrors";
import { CanvasWrapper } from "./CanvasWrapper";
import { ChatInfoDialog } from "./Chat/ChatInfoDialog";
import { Chat, ChatContext } from "./Chat/utils";
import { ColorPickFeedback } from "./ColorPickFeedback";
import { DebugModal } from "./Debug/DebugModal";
import { DynamicModals } from "./DynamicModals";
import { EventInfoModal } from "./EventInfo/EventInfo";
import { Header } from "./Header/Header";
import { InfoSidebar } from "./Info/InfoSidebar";
import { MailingListsModal } from "./Info/MailingListsModal";
import { KeybindModal } from "./KeybindModal";
import { LoginModal } from "./Login/LoginModal";
import { PixelWhoisSidebar } from "./PixelSidebar/PixelWhoisSidebar";
import { ProfileModal } from "./Profile/ProfileModal";
import { SettingsSidebar } from "./Settings/SettingsSidebar";
import { ToastWrapper } from "./ToastWrapper";
import { ToolbarWrapper } from "./Toolbar/ToolbarWrapper";

console.log("Client init with version " + __COMMIT_HASH__);

// get access to context data
const AppInner = () => {
  const { config, settings } = useAppContext();
  const Panel = usePanel();
  const keybindingsEnabled = settings.get["keybindings.enabled"];

  const [uiHidden, setUiHidden] = useState(false);

  useEffect(() => {
    KeybindManager.setEnabled(keybindingsEnabled);
  }, [keybindingsEnabled]);

  useEffect(() => {
    const toggleUi = () => setUiHidden((hidden) => !hidden);

    KeybindManager.on("TOGGLE_UI", toggleUi);

    return () => {
      KeybindManager.off("TOGGLE_UI", toggleUi);
    };
  }, []);

  useEffect(() => {
    const toggleSettings = () =>
      Panel.dispatch(["settings", Panel.state.settings ? undefined : true]);
    const toggleInfo = () =>
      Panel.dispatch(["info", Panel.state.info ? undefined : true]);

    KeybindManager.on("TOGGLE_SETTINGS", toggleSettings);
    KeybindManager.on("TOGGLE_INFO", toggleInfo);

    return () => {
      KeybindManager.off("TOGGLE_SETTINGS", toggleSettings);
      KeybindManager.off("TOGGLE_INFO", toggleInfo);
    };
  }, [Panel.dispatch, Panel.state.info, Panel.state.settings]);

  return (
    <>
      {config && <CanvasWrapper />}
      <ColorPickFeedback />
      <div
        id="main-ui"
        aria-hidden={uiHidden}
        inert={uiHidden}
        className={["w-screen h-svh", uiHidden && "main-ui--hidden"]
          .filter(Boolean)
          .join(" ")}
      >
        <Header className="w-full" style={{ gridArea: "header" }} />
        <ToolbarWrapper
          style={{
            gridArea: "toolbar",
          }}
        />
        {/* <div className="bg-amber-500" style={{ gridArea: "lsidebar" }} />
        <div className="bg-amber-500" style={{ gridArea: "rsidebar" }} /> */}

        <Chat />

        <DebugModal />
        <SettingsSidebar />
        <InfoSidebar />
        <PixelWhoisSidebar />
        <KeybindModal />
        <AuthErrors />

        <ProfileModal />
        <ChatInfoDialog />
        <LoginModal />
        <Moderator />

        <ToastWrapper />
        <DynamicModals />
        <EventInfoModal />
        <MailingListsModal />
      </div>
    </>
  );
};

const App = () => {
  return (
    <ChatContext>
      <SWRConfig
        value={{
          fetcher: (resource, init) =>
            fetch(resource, init).then((res) => res.json()),
        }}
      >
        <AppContext>
          <AudioContext>
            <PanelContext>
              <DialogContext>
                <ModeratorContext>
                  <TemplateContext>
                    <CaptureContext>
                      <AppInner />
                    </CaptureContext>
                  </TemplateContext>
                </ModeratorContext>
              </DialogContext>
            </PanelContext>
          </AudioContext>
        </AppContext>
      </SWRConfig>
    </ChatContext>
  );
};

export default App;
